import React, { useState, useEffect, useRef } from 'react';
import { CardNumberElement, CardExpiryElement, CardCvcElement, useElements, useStripe } from '@stripe/react-stripe-js';
import styled from 'styled-components';
import { FiLock, FiCreditCard, FiCalendar, FiShield } from 'react-icons/fi';
import './Payment.css';

const CardFormContainer = styled.div`
  margin-bottom: 1.5rem;
`;

const CardInputRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
  
  @media (min-width: 768px) {
    grid-template-columns: ${props => props.columns || '1fr 1fr'};
  }
`;

const CardInputGroup = styled.div`
  margin-bottom: 1rem;
`;

const CardInputLabel = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 8px;
    color: var(--primary);
  }
`;

const CardInputContainer = styled.div`
  padding: 14px 16px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background-color: white;
  transition: border-color 0.3s ease;
  height: 54px;
  display: flex;
  align-items: center;
  
  &:focus-within {
    outline: none;
  }
  
  ${props => props.error && `
    border-color: #D13046;
    background-color: rgba(45, 49, 66, 0.02);
  `}
`;

const CardNumberContainer = styled(CardInputContainer)`
  width: 100%;
`;

const ExpiryContainer = styled(CardInputContainer)`
  width: 100%;
`;

const CvcContainer = styled(CardInputContainer)`
  width: 100%;
`;

const ErrorMessage = styled.div`
  color: #D13046;
  font-size: 0.85rem;
  margin-top: 0.5rem;
`;

const SecurePaymentNotice = styled.div`
  display: flex;
  align-items: center;
  color: var(--gray, #6C757D);
  font-size: 14px;
  margin-bottom: 1.5rem;
  
  svg {
    margin-right: 8px;
    color: var(--secondary, #7ECBB9);
  }
`;

const AcceptedCards = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 1rem;
  flex-wrap: wrap;
  
  img {
    height: 30px;
    object-fit: contain;
  }
`;

const SecurityNote = styled.div`
  font-size: 0.85rem;
  color: var(--gray, #6C757D);
  margin-top: 1rem;
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 0.5rem;
  }
`;

const FieldHint = styled.div`
  font-size: 0.8rem;
  color: #6c757d;
  margin-top: 0.25rem;
  margin-bottom: 0.5rem;
`;

/**
 * StripeCardForm component for collecting credit card information
 * This component provides a more detailed form with separate fields for
 * card number, expiration date, and CVC
 */
const StripeCardForm = ({ onChange, errors }) => {
  const stripe = useStripe();
  const elements = useElements();
  
  // Local state to track element completion
  const [elementStatus, setElementStatus] = useState({
    cardNumber: false,
    cardExpiry: false,
    cardCvc: false
  });
  
  // Use ref to prevent infinite loops
  const prevStatusRef = useRef(null);
  
  // Common styles for all card elements
  const cardElementStyle = {
    style: {
      base: {
        fontSize: '16px',
        color: '#2D3142',
        fontFamily: 'Nunito, sans-serif',
        fontSmoothing: 'antialiased',
        '::placeholder': {
          color: '#aab7c4',
        },
        iconColor: '#E94057',
      },
      invalid: {
        color: '#D13046',
        iconColor: '#D13046',
      },
    },
  };
  
  // Handle changes to card elements
  const handleCardElementChange = (event) => {
    const { elementType, complete, error } = event;
    
    // Update local state
    setElementStatus(prev => ({
      ...prev,
      [elementType]: complete
    }));
  };
  
  // Update parent component when element status changes
  useEffect(() => {
    const isComplete = elementStatus.cardNumber && elementStatus.cardExpiry && elementStatus.cardCvc;
    
    // Check if status has actually changed to avoid infinite loops
    const prevStatus = prevStatusRef.current;
    const statusChanged = 
      !prevStatus || 
      prevStatus.cardNumber !== elementStatus.cardNumber ||
      prevStatus.cardExpiry !== elementStatus.cardExpiry ||
      prevStatus.cardCvc !== elementStatus.cardCvc;
    
    if (statusChanged) {
      // Update the ref with current status
      prevStatusRef.current = {...elementStatus};
      
      // Notify parent component
      onChange({
        error: null,
        elementComplete: elementStatus,
        complete: isComplete
      });
    }
  }, [elementStatus]);
  
  return (
    <CardFormContainer>
      <SecurePaymentNotice>
        <FiLock /> Your payment information is secure and encrypted. We never store your full card details. Secure payment powered by Stripe
      </SecurePaymentNotice>
      
      <CardInputGroup>
        <CardInputLabel htmlFor="card-number">
          <FiCreditCard /> Card Number
        </CardInputLabel>
        <FieldHint>Enter the 16-digit number on the front of your card</FieldHint>
        <CardNumberContainer error={errors?.cardNumber}>
          <CardNumberElement 
            id="card-number"
            options={{
              ...cardElementStyle,
              showIcon: true,
              iconStyle: 'solid'
            }}
            onChange={(e) => handleCardElementChange({...e, elementType: 'cardNumber'})}
          />
        </CardNumberContainer>
        {errors?.cardNumber && <ErrorMessage>{errors.cardNumber}</ErrorMessage>}
      </CardInputGroup>
      
      <CardInputRow>
        <CardInputGroup>
          <CardInputLabel htmlFor="card-expiry">
            <FiCalendar /> Expiration Date
          </CardInputLabel>
          <FieldHint>MM / YY format</FieldHint>
          <ExpiryContainer error={errors?.cardExpiry}>
            <CardExpiryElement 
              id="card-expiry"
              options={cardElementStyle}
              onChange={(e) => handleCardElementChange({...e, elementType: 'cardExpiry'})}
            />
          </ExpiryContainer>
          {errors?.cardExpiry && <ErrorMessage>{errors.cardExpiry}</ErrorMessage>}
        </CardInputGroup>
        
        <CardInputGroup>
          <CardInputLabel htmlFor="card-cvc">
            <FiShield /> Security Code (CVC)
          </CardInputLabel>
          <FieldHint>3 or 4 digits, usually on the back of your card</FieldHint>
          <CvcContainer error={errors?.cardCvc}>
            <CardCvcElement 
              id="card-cvc"
              options={cardElementStyle}
              onChange={(e) => handleCardElementChange({...e, elementType: 'cardCvc'})}
            />
          </CvcContainer>
          {errors?.cardCvc && <ErrorMessage>{errors.cardCvc}</ErrorMessage>}
        </CardInputGroup>
      </CardInputRow>
    </CardFormContainer>
  );
};

export default StripeCardForm;
